import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CardConfiguration, CardSubType, CardCodeType, OrganisationConfiguration, CardType, ConnectorTag } from '@bciti/types';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BcitiEnvironment } from 'src/app/interfaces/store/store.interface';
import { State } from 'src/app/store';
import { environmentSelector } from 'src/app/store/global/global.selectors';
import { AddCardDialogComponent } from './dialogs/add-card-dialog/add-card-dialog.component';
import { MatTabGroup } from '@angular/material/tabs';

@Component({
    selector: 'app-cards-form',
    templateUrl: './cards-form.component.html',
    styleUrls: ['./cards-form.component.scss'],
})
export class CardsFormComponent implements OnInit, OnDestroy, OnChanges {
    constructor(private fb: FormBuilder, private store: Store<State>, private dialog: MatDialog) {}
    private _selected: number = 0;

    get selected() {
        return this._selected;
    }

    set selected(v: number) {
        this._selected = v;
    }

    @ViewChild('tabGroup', { static: false })
    tabGroup: MatTabGroup;

    @Input()
    loadedCards: CardConfiguration[];

    @Input()
    organisationConfig: OrganisationConfiguration;

    @Input()
    loading: boolean = false;

    @Input()
    displayActions: boolean = true;

    @Input()
    errors: Array<object>;

    @Output()
    submitForm = new EventEmitter<CardConfiguration[]>();

    destroy$ = new Subject();
    availableLangs: Array<string>;
    cardArray: Array<FormGroup>;
    cardSubTypes: CardSubType[] = Object.values(CardSubType);
    cardCodeTypes: CardCodeType[] = Object.values(CardCodeType);
    env: Partial<BcitiEnvironment>;
    clickedEvent: Event;
    icons: string[] = [
        'icon-activities',
        'icon-assembly',
        'icon-biblio',
        'icon-calendar',
        'icon-city',
        'icon-compass',
        'icon-construction',
        'icon-consultation',
        'icon-discover',
        'icon-envelope',
        'icon-family',
        'icon-faq',
        'icon-graffiti',
        'icon-home',
        'icon-ice-rink',
        'icon-image',
        'icon-info',
        'icon-map',
        'icon-my-card',
        'icon-no-parking',
        'icon-notifications',
        'icon-parking-meter',
        'icon-parking',
        'icon-phone-alerts',
        'icon-pot-hole',
        'icon-privacy',
        'icon-question',
        'icon-receipt',
        'icon-recycling-bin',
        'icon-requests',
        'icon-restaurant',
        'icon-shop',
        'icon-slippery-ice',
        'icon-snow',
        'icon-snowblower',
        'icon-street-lamp',
        'icon-tag',
        'icon-tennis',
        'icon-theatre',
        'icon-timer',
        'icon-town-hall',
        'icon-trash-bin',
        'icon-tree',
        'icon-warning',
        'icon-water-in-service',
        'icon-water-service-interruption',
    ];

    childEventClicked(event: Event) {
        this.clickedEvent = event;
    }

    ngOnInit(): void {
        this.loading = true;
        this.store
            .select(environmentSelector)
            .pipe(tap((env) => (this.env = env)))
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.loading = true;
        if (!!changes?.organisationConfig?.currentValue) {
            this.loadedCards = changes.organisationConfig.currentValue.support?.cards ?? [];
            this.cardArray = this.loadedCards.map((card) => this.generateCardForm(card));
            this.availableLangs = this.organisationConfig.lang.available;
        }
        this.loading = false;
    }

    openCreateCardDialog() {
        this.dialog
            .open(AddCardDialogComponent, {
                panelClass: 'add-card-dialog',
                data: { cardsInUse: this.cardArray },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.cardArray.push(
                        this.generateCardForm({
                            barcode: '',
                            lifespan: 0,
                            proofOfIdentityCount: 0,
                            regex: '',
                            regexBarcode: '',
                            renewalDelay: 0,
                            startSequence: '',
                            prefix: '',
                            suffix: '',
                            type: result,
                            linkSystExt: false,
                            requiredCards: [],
                            enableCards: [],
                            availableSubTypes: [],
                            requiredSubTypes: [],
                            creatable: true,
                            linkable: false,
                            identity: {
                                label: {
                                    fr: result,
                                    en: result,
                                },
                                icon: '',
                                instructions: {},
                            },
                            tooltips: {
                                subscribe: {
                                    fr: '',
                                    en: '',
                                },
                                link: {
                                    fr: '',
                                    en: '',
                                },
                            },
                            autoApprove: false,
                            sequenceGenerator: false,
                            isConnected: true,
                            dynamicSuffixHandler: {
                                defaultValue: '',
                                suffixLength: 0,
                                codeConnectorPriority: null,
                            },
                        }),
                    );

                    setTimeout(() => {
                        this.tabGroup.selectedIndex = this.cardArray.length - 1;
                        this._selected = 0;
                    });
                }
            });
    }

    private generateCardForm(cardConfiguration: CardConfiguration) {
        const cardForm = this.fb.group({
            barcode: [cardConfiguration.barcode],
            lifespan: [cardConfiguration.lifespan, Validators.min(0)],
            proofOfIdentityCount: [cardConfiguration.proofOfIdentityCount, Validators.min(1)],
            regex: cardConfiguration.regex ? [cardConfiguration.regex] : [''],
            regexBarcode: cardConfiguration.regexBarcode ? [cardConfiguration.regexBarcode] : [''],
            renewalDelay: [cardConfiguration.renewalDelay, Validators.min(0)],
            startSequence: [cardConfiguration.startSequence],
            prefix: [cardConfiguration.prefix],
            suffix: [cardConfiguration.suffix],
            type: [cardConfiguration.type],
            linkSystExt: cardConfiguration.linkSystExt ? [cardConfiguration.linkSystExt] : [false],
            requiredCards: [cardConfiguration.requiredCards],
            enableCards: [cardConfiguration.enableCards],
            availableSubTypes: [cardConfiguration.availableSubTypes],
            requiredSubTypes: [cardConfiguration.requiredSubTypes],
            linkable: cardConfiguration.linkable ? [cardConfiguration.linkable] : [false],
            identity: this.fb.group({
                label: this.fb.group({}),
                icon: [cardConfiguration.identity.icon],
                instructions: this.fb.group({
                    general: this.fb.group({
                        introduction: this.fb.group({}),
                    }),
                    steps: this.fb.group({
                        info: this.fb.group({}),
                        attachments: this.fb.group({}),
                        confirmation: this.fb.group({}),
                    }),
                }),
            }),
            tooltips: this.fb.group({
                subscribe: this.fb.group({}),
                link: this.fb.group({}),
            }),
            autoApprove: cardConfiguration.autoApprove ? [cardConfiguration.autoApprove] : [false],
            sequenceGenerator: cardConfiguration.sequenceGenerator ? [cardConfiguration.sequenceGenerator] : [false],
            isConnected: [cardConfiguration.isConnected],
            dynamicSuffixHandler: this.fb.group({
                defaultValue: cardConfiguration.dynamicSuffixHandler?.defaultValue,
                suffixLength: cardConfiguration.dynamicSuffixHandler?.suffixLength,
                codeConnectorPriority: cardConfiguration.dynamicSuffixHandler?.codeConnectorPriority,
            }),
        });

        const labelGroup = cardForm.get('identity.label') as FormGroup;
        const introductionGroup = cardForm.get('identity.instructions.general.introduction') as FormGroup;

        const infoGroup = cardForm.get('identity.instructions.steps.info') as FormGroup;

        const attachmentsGroup = cardForm.get('identity.instructions.steps.attachments') as FormGroup;

        const confirmationGroup = cardForm.get('identity.instructions.steps.confirmation') as FormGroup;

        const subscribeGroup = cardForm.get('tooltips.subscribe') as FormGroup;

        const linkGroup = cardForm.get('tooltips.link') as FormGroup;

        this.organisationConfig.lang.available.forEach((lang) => {
            labelGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'identity', 'label', lang) ?? ''));
            introductionGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'identity', 'instructions', 'general', 'introduction', lang) ?? ''));
            infoGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'identity', 'instructions', 'steps', 'info', lang) ?? ''));
            attachmentsGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'identity', 'instructions', 'steps', 'attachments', lang) ?? ''));
            confirmationGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'identity', 'instructions', 'steps', 'confirmation', lang) ?? ''));
            subscribeGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'tooltips', 'subscribe', lang) ?? ''));
            linkGroup.addControl(lang, this.fb.control(this.getNested(cardConfiguration, 'tooltips', 'link', lang) ?? ''));
        });

        return cardForm;
    }

    submit() {
        const payload = this.cardArray.map((card) => card.value as CardConfiguration);

        this.submitForm.emit(payload as CardConfiguration[]);
    }

    getCardTypes(currentCardType: string): string[] {
        return Object.values(CardType).filter((cardType) => cardType !== currentCardType);
    }

    // Checks the error object from cards.components
    // |- specificProperty: a specific key (ex: cardsRequired)
    // |- tabNumber: a whole card tab (1st card = 0, 2nd card = 1, etc.)
    // These params are exclusive
    hasErrors(specificProperty: string = '', tabNumber: number = null) {
        let hasErrors = false;
        const tabToCheck = tabNumber != null && tabNumber >= 0 && tabNumber < this.errors?.length ? tabNumber : this.selected;

        if (this.errors?.length > 0) {
            if (specificProperty) {
                hasErrors = specificProperty in this.errors[tabToCheck] && this.errors[tabToCheck][specificProperty];
            } else {
                for (const prop in this.errors[tabToCheck]) {
                    if (this.errors[tabToCheck][prop]) {
                        hasErrors = true;
                        break;
                    }
                }
            }
        }
        return hasErrors;
    }

    get connectorTags() {
        return Object.values(ConnectorTag);
    }

    // https://stackoverflow.com/questions/2631001/test-for-existence-of-nested-javascript-object-key
    private getNested(initialObj, ...args) {
        return args.reduce((obj, level) => obj && obj[level], initialObj);
    }
}
